// Fires a confetti burst whenever `trigger` changes to a new truthy value. The confetti is ours: tiny hearts, pluses,
// capsules and bolts among ribbons and dots, in the ward palette. Each piece is a small DOM node flying a ballistic
// path with drag, sway and flutter, precomputed as Web Animations keyframes (transform and opacity only), so the
// compositor flies it even while the page is busy. Pieces clean themselves up. Reduced motion: no confetti (the
// screen flash and the tile carry the moment).
//   origin: 'center' (burst up from the middle), 'top' (rain), 'sides' (two cannons, the podium), or { x, y } in
//   viewport px (or fractions of the viewport when both are 1 or less), e.g. the centre of the tile that was right.
//   life: how long a piece flies, in seconds [shortest, longest]; staggerMs: the pieces leave over this long, so a
//   podium's cannons keep firing rather than emptying in one go.
import { createContext, useContext } from 'react';
import { useFire } from './fire';
import { canAnimate, prefersReducedMotion } from './reduced';
import { GLYPHS } from '@/brand/marks';

type Origin = 'center' | 'top' | 'sides' | { x: number; y: number };
const DEFAULT_COLORS = ['var(--brand)', 'var(--lime)', 'var(--cart)', 'var(--iv)', 'var(--mark-heart)', 'var(--mark-bolt)', 'var(--brand-soft)'];

export interface ConfettiOptions { life?: [number, number]; staggerMs?: number }

/** 30 Oct 2026: true where a burst would fly over the wrong page (the Console's On air preview): no confetti there. */
export const ConfettiOff = createContext(false);

export function ConfettiBurst({ trigger, origin = 'center', count = 80, colors, life, staggerMs }: { trigger: unknown; origin?: Origin; count?: number; colors?: string[] } & ConfettiOptions) {
  const off = useContext(ConfettiOff);
  useFire(trigger, () => { if (!off && canAnimate() && !prefersReducedMotion()) burst(origin, count, colors?.length ? colors : DEFAULT_COLORS, { life, staggerMs }); });
  return null;
}

let layer: HTMLDivElement | null = null;
function getLayer(): HTMLDivElement {
  if (layer && layer.isConnected) return layer;
  layer = document.createElement('div');
  layer.setAttribute('aria-hidden', 'true');
  layer.dataset.rdConfetti = '';
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:70;contain:strict';
  document.body.appendChild(layer);
  return layer;
}

// Deterministic-enough randomness without Math.random's cost being an issue; seeded per burst for variety.
function rng(seed: number) { let s = seed >>> 0 || 1; return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) % 100000) / 100000; }; }
let bursts = 0;

const MARK_SHAPES = ['heart', 'plus', 'capsule', 'bolt'] as const;
function piece(kind: 'ribbon' | 'dot' | 'mark', color: string, size: number, mark: typeof MARK_SHAPES[number]): HTMLElement {
  if (kind === 'mark') {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', String(size)); svg.setAttribute('height', String(size));
    const d = mark === 'capsule' ? [GLYPHS.capsuleA, GLYPHS.capsuleB] : [GLYPHS[mark]];
    const g = document.createElementNS(ns, 'g'); if (mark === 'capsule') g.setAttribute('transform', 'rotate(-45 12 12)');
    d.forEach((p, i) => { const path = document.createElementNS(ns, 'path'); path.setAttribute('d', p); path.setAttribute('fill', color); if (i) path.setAttribute('fill-opacity', '0.55'); g.appendChild(path); });
    svg.appendChild(g);
    const wrap = document.createElement('span'); wrap.appendChild(svg); wrap.style.cssText = `position:absolute;left:0;top:0;width:${size}px;height:${size}px;margin:${-size / 2}px 0 0 ${-size / 2}px;will-change:transform,opacity`;
    return wrap;
  }
  const el = document.createElement('span');
  const w = kind === 'ribbon' ? size * 0.42 : size * 0.6; const h = kind === 'ribbon' ? size : size * 0.6;
  el.style.cssText = `position:absolute;left:0;top:0;width:${w}px;height:${h}px;margin:${-h / 2}px 0 0 ${-w / 2}px;background:${color};border-radius:${kind === 'dot' ? '50%' : '2px'};will-change:transform,opacity`;
  return el;
}

function burst(origin: Origin, count: number, colors: string[], opts: ConfettiOptions = {}) {
  const W = window.innerWidth; const H = window.innerHeight; const scale = Math.max(0.6, Math.min(1.6, Math.min(W, H) / 760));
  const rand = rng(Date.now() ^ (++bursts * 0x9e3779b1));
  const host = getLayer();
  type Src = { x: number; y: number; angle: number; spread: number; speed: [number, number]; n: number; life: [number, number] };
  const deg = Math.PI / 180;
  let sources: Src[];
  if (origin === 'sides') {
    const n = Math.round(count / 2);
    sources = [
      { x: -8, y: H * 0.9, angle: -62 * deg, spread: 16 * deg, speed: [900, 1500], n, life: opts.life ?? [2.2, 3.2] },
      { x: W + 8, y: H * 0.9, angle: -118 * deg, spread: 16 * deg, speed: [900, 1500], n: count - n, life: opts.life ?? [2.2, 3.2] },
    ];
  } else if (origin === 'top') {
    sources = [{ x: W / 2, y: -20, angle: 90 * deg, spread: 70 * deg, speed: [60, 260], n: count, life: opts.life ?? [2.4, 3.4] }];
  } else {
    const p = origin === 'center' ? { x: W / 2, y: H * 0.45 } : origin.x <= 1 && origin.y <= 1 ? { x: origin.x * W, y: origin.y * H } : origin;
    sources = [{ x: p.x, y: p.y, angle: -90 * deg, spread: 75 * deg, speed: [520, 1080], n: count, life: opts.life ?? [1.5, 2.4] }];
  }
  const stagger = opts.staggerMs ?? (origin === 'sides' ? 260 : 60);
  const g = 1450 * scale; const FRAMES = 18;
  for (const s of sources) {
    for (let i = 0; i < s.n; i++) {
      const r = rand();
      const kind = r < 0.42 ? 'ribbon' : r < 0.66 ? 'dot' : 'mark';
      const size = (kind === 'mark' ? 11 + rand() * 6 : kind === 'ribbon' ? 11 + rand() * 6 : 7 + rand() * 4) * Math.min(1.25, scale);
      const color = colors[Math.floor(rand() * colors.length)];
      const el = piece(kind, color, size, MARK_SHAPES[Math.floor(rand() * 4)]);
      const startX = origin === 'top' ? rand() * W : s.x; const startY = s.y;
      const a = s.angle + (rand() * 2 - 1) * s.spread;
      const v = (s.speed[0] + rand() * (s.speed[1] - s.speed[0])) * scale;
      const vx = Math.cos(a) * v; const vy = Math.sin(a) * v;
      const k = kind === 'ribbon' ? 3.6 : kind === 'dot' ? 2.6 : 3.1; // drag: paper floats, dots fall faster
      const life = s.life[0] + rand() * (s.life[1] - s.life[0]);
      const sway = (10 + rand() * 22) * scale; const swayF = 1.2 + rand() * 1.6; const phase = rand() * Math.PI * 2;
      const spin = (rand() < 0.5 ? -1 : 1) * (240 + rand() * 600); const rot0 = rand() * 360;
      const flipF = kind === 'dot' ? 0 : 1.5 + rand() * 2.5;
      const frames: Keyframe[] = [];
      for (let f = 0; f <= FRAMES; f++) {
        const t = (f / FRAMES) * life;
        const e = Math.exp(-k * t);
        const x = startX + (vx * (1 - e)) / k + sway * Math.sin(swayF * Math.PI * 2 * t + phase) * Math.min(1, t / 0.5);
        const y = startY + (g / k) * t + ((vy - g / k) * (1 - e)) / k;
        const rot = rot0 + spin * t * (0.6 + 0.4 * e);
        const sy = flipF ? Math.cos(flipF * Math.PI * 2 * t) : 1;
        const o = t < life * 0.72 ? 1 : Math.max(0, 1 - (t - life * 0.72) / (life * 0.28));
        frames.push({ transform: `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) rotate(${rot.toFixed(1)}deg) scale(1,${sy.toFixed(3)})`, opacity: o, offset: f / FRAMES });
      }
      host.appendChild(el);
      const anim = el.animate(frames, { duration: life * 1000, easing: 'linear', fill: 'forwards', delay: rand() * stagger });
      anim.onfinish = () => el.remove(); anim.oncancel = () => el.remove();
    }
  }
}
